import { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import instance from "../../Interceptor/api_instance";
import {
  Spinner,
  TablePlaceHolder,
} from "../../Components/LoadingAnimations/LoadingAnimations";
import AddItem from "./AddItem";
import DebouncedInput from "../../Components/Inputs/DebouncedInput";
import RowActions from "./RowActions";

function getCursorFromUrl(nextUrl) {
  if (!nextUrl) return undefined;
  const url = nextUrl.startsWith("http")
    ? new URL(nextUrl)
    : new URL(nextUrl, window.location.origin);
  return url.searchParams.get("cursor") || undefined;
}

const InventoryTable = ({ location }) => {
  const { t } = useTranslation();

  const [selectedRow, setSelectedRow] = useState(null);
  const [addItem, setAddItem] = useState(false);
  const [itemName, setItemName] = useState(null);

  const setFilter = (name, value) => {
    // Only react when the filter actually changes. Some child inputs
    // may call this on mount with the same value which would otherwise
    // clear the cache and set status back to "idle" after a load.
    if (value === itemName) return;

    // Abort any in-flight request for this tab, reset cached items
    controllerRef.current?.abort();
    setCache((s) => ({
      ...s,
      [location]: {
        ...s[location],
        items: [],
        nextCursor: undefined,
        hasMore: true,
        status: "idle",
        error: null,
      },
    }));

    // Update the filter value which will trigger the effect to refetch
    setItemName(value);
  }
 
  const [cache, setCache] = useState({
    "San Diego": {
      id: 1,
      items: [],
      nextCursor: undefined,
      hasMore: true,
      status: "idle",
      error: null,
    },
    Tijuana: {
      id: 2,
      items: [],
      nextCursor: undefined,
      hasMore: true,
      status: "idle",
      error: null,
    },
    Toyota: {
      id: 20,
      items: [],
      nextCursor: undefined,
      hasMore: true,
      status: "idle",
      error: null
    },
    Velocity: {
      id: 23,
      items: [],
      nextCursor: undefined,
      hasMore: true,
      status: "idle",
      error: null,
    },
    Mexicali: {
      id: 22,
      items: [],
      nextCursor: undefined,
      hasMore: true,
      status: "idle",
      error: null,
    },
  });

  const scrollRef = useRef(null); // <-- scrollable container
  const controllerRef = useRef(null);
  const sentinelRef = useRef(null); // <-- bottom sentinel

  const entry = cache[location];

  const loadMore = useCallback(async () => {
    if (!entry.hasMore || entry.status === "loading") return;

    controllerRef.current?.abort();
    controllerRef.current = new AbortController();

    setCache((s) => ({
      ...s,
      [location]: { ...s[location], status: "loading", error: null },
    }));

    try {
      const params = new URLSearchParams();
      params.set("location", String(entry.id));
      if (entry.nextCursor) params.set("cursor", entry.nextCursor);
      if (itemName) params.set("name", itemName);

      const res = await instance(
        `/safety/get-inventory?paginate=true&${params}`,
        {
          signal: controllerRef.current.signal,
        }
      );
      const page = res.data; // expect { results: [...], next: "url or null" }

      setCache((s) => ({
        ...s,
        [location]: {
          ...s[location],
          items: [...s[location].items, ...(page.results || [])],
          nextCursor: getCursorFromUrl(page.next),
          hasMore: !!page.next, // <-- DRF style
          status: "success",
          error: null,
        },
      }));
    } catch (e) {
      if (e.name === "AbortError") return;
      setCache((s) => ({
        ...s,
        [location]: {
          ...s[location],
          status: "error",
          error: e?.message || String(e),
        },
      }));
    }
  }, [location, entry.hasMore, entry.status, entry.nextCursor, entry.id, itemName]);

  // Initial load per tab
  useEffect(() => {
    if (entry.status === "idle") loadMore();
    return () => controllerRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, itemName]);
  // Observe the sentinel within the scroll container
  useEffect(() => {
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return;

    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) loadMore();
      },
      {
        root, // <-- critical for inner scrolling
        rootMargin: "0px 0px 300px 0px", // prefetch a bit early
        threshold: 0,
      }
    );

    io.observe(target);
    return () => io.disconnect();
  }, [loadMore]);

  if (entry.status === "error" && entry.items.length === 0) {
    return <div>Error: {entry.error}</div>;
  }

  return (
    <>
      {selectedRow && (
        // <TransferStock
        //   location={location}
        //   row={selectedRow}
        //   close={() => setSelectedRow(null)}
        // />
        <RowActions 
        row={selectedRow} 
        location={location}
        close={() => setSelectedRow(null)}
        onUpdateStock={(updated) => {
          setCache((s) => ({
            ...s,
            [location]: {
              ...s[location],
              items: s[location].items.map((it) => (it.id === updated.id ? updated : it)),
            },
          }));
        }}
        />
      )}
      {addItem && (
        <AddItem close={() => setAddItem(false)}/>
      )}
      {entry.status === "idle" && entry.items.length === 0 ? (
        <TablePlaceHolder />
      ) : (
        <>
          <div className="search-filters-cont">
            <label>{t("yard-tables.search-bar")}: <DebouncedInput state={itemName} setState={setFilter} label={"name"} name="name"/></label>
            <button className="change-pwd-btn all-btn" onClick={() => setAddItem(true)}>
              Add Item
            </button>
          </div>

          {/* This div must have overflow: auto (your CSS .table-cont already does) */}
          <div className="table-cont" ref={scrollRef}>
            <table id="forwardTable" className="yard-table">
              <thead>
                <tr className="table-header">
                  <th>{t("yard-tables.table-columns.code")}</th>
                  <th>{t("yard-tables.table-columns.name")}</th>
                  <th>{t("forms.type.maintenance-report.labels.quantity")}</th>
                </tr>
              </thead>
              <tbody>
                {entry.items.map((item) => (
                  <tr
                    key={item.id}
                    className="data-rows row-hover"
                    data-testid={`row-${item.id}`}
                    onClick={() => setSelectedRow(item)}
                  >
                    <td className="gray">{item.part.sku}</td>
                    <td className="gray">{item.part.name}</td>
                    <td className="gray">{item.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Sentinel MUST be outside the table but inside the scroll container */}
            <div ref={sentinelRef} style={{ height: 1 }} />

            {/* Footer states */}
            {entry.status === "loading" && (
              <div style={{ padding: 12, textAlign: "center" }}>
                <Spinner />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default InventoryTable;
